import type { MutableRefObject } from 'react';

import type { RxMedication } from '@/entities/rx';

import type { RxFormApi } from './types';

export type ApplySelectedMedicationResult = {
  label: string | null;
  dispenseUnitId: string;
};

function toLabel(selected: RxMedication): string | null {
  return selected.DisplayName ?? selected.DrugName ?? null;
}

function toNextDispenseUnitId(selected: RxMedication): string | null {
  const medicationDispenseUnitId = selected.DispenseUnitId;
  if (
    medicationDispenseUnitId == null ||
    !Number.isFinite(medicationDispenseUnitId) ||
    medicationDispenseUnitId <= 0
  ) {
    return null;
  }
  return String(medicationDispenseUnitId);
}

function canAutoUpdateDispenseUnit(current: string, lastAuto: string | null): boolean {
  if (current.length === 0) return true;
  return lastAuto != null && current === lastAuto;
}

export function applySelectedMedicationToForm(args: {
  form: RxFormApi;
  selected: RxMedication;
  lastAutoDispenseUnitIdRef?: MutableRefObject<string | null>;
}): ApplySelectedMedicationResult {
  const { form, selected, lastAutoDispenseUnitIdRef } = args;

  form.setFieldValue('selectedMedication', selected);

  const currentDispenseUnitId = form.state.values.dispenseUnitId?.trim() ?? '';
  const shouldAutoUpdate = canAutoUpdateDispenseUnit(
    currentDispenseUnitId,
    lastAutoDispenseUnitIdRef?.current ?? null
  );

  if (shouldAutoUpdate) {
    const nextDispenseUnitId = toNextDispenseUnitId(selected);
    if (nextDispenseUnitId != null) {
      form.setFieldValue('dispenseUnitId', nextDispenseUnitId);
      if (lastAutoDispenseUnitIdRef) {
        lastAutoDispenseUnitIdRef.current = nextDispenseUnitId;
      }
    } else if (currentDispenseUnitId.length > 0) {
      form.setFieldValue('dispenseUnitId', '');
      if (lastAutoDispenseUnitIdRef) {
        lastAutoDispenseUnitIdRef.current = null;
      }
    }
  }

  return {
    dispenseUnitId: form.state.values.dispenseUnitId,
    label: toLabel(selected),
  };
}
